import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter
} from "@/components/ui/dialog";
import { Plus, Phone, Mail, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["New", "Contacted", "Qualified", "Proposal", "Won", "Lost"];

export default function CRM() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ status: "New" });

  const load = useCallback(async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      await api.post("/leads", form);
      toast.success("Lead added");
      setOpen(false);
      setForm({ status: "New" });
      load();
    } catch { toast.error("Failed"); }
  };

  const move = async (lead, status) => {
    await api.put(`/leads/${lead.id}`, { ...lead, status });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete lead?")) return;
    await api.delete(`/leads/${id}`);
    load();
  };

  const byStage = STAGES.reduce((acc, s) => { acc[s] = leads.filter((l) => l.status === s); return acc; }, {});

  return (
    <div className="space-y-6" data-testid="crm-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">CRM · Lead Pipeline</h1>
          <p className="text-sm text-muted-foreground mt-1">Track leads, follow-ups, and conversions</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-lead-btn"><Plus size={16} className="mr-1" /> Add Lead</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {STAGES.map((stage) => (
          <div key={stage} className="bg-muted/40 rounded-xl p-3 min-h-[400px]" data-testid={`stage-${stage}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading font-semibold text-sm">{stage}</div>
              <Badge variant="secondary" className="text-xs">{byStage[stage].length}</Badge>
            </div>
            <div className="space-y-2">
              {byStage[stage].map((l) => (
                <Card key={l.id} className="p-3 hover:shadow-sm transition-all" data-testid={`lead-${l.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-sm">{l.name}</div>
                    <Button variant="ghost" size="icon" className="h-6 w-6 -mr-1 -mt-1 text-destructive" onClick={() => remove(l.id)}><Trash2 size={12} /></Button>
                  </div>
                  {l.company && <div className="text-xs text-muted-foreground mt-0.5">{l.company}</div>}
                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    {l.phone && <span className="flex items-center gap-1"><Phone size={10} /> {l.phone}</span>}
                    {l.email && <span className="flex items-center gap-1"><Mail size={10} /> {l.email}</span>}
                  </div>
                  {l.value && <div className="mt-2 text-xs font-semibold text-primary">₹{Number(l.value).toLocaleString("en-IN")}</div>}
                  <div className="flex gap-1 mt-2">
                    {STAGES.filter((s) => s !== l.status).map((s) => (
                      <button key={s} onClick={() => move(l, s)} className="text-[10px] px-1.5 py-0.5 rounded bg-muted hover:bg-primary hover:text-primary-foreground transition-colors">
                        {s}
                      </button>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Lead</DialogTitle></DialogHeader>
          <div className="grid gap-3 mt-2">
            <div><Label>Name *</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div><Label>Company</Label><Input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Value (₹)</Label><Input type="number" value={form.value || ""} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) || 0 })} /></div>
              <div>
                <Label>Status</Label>
                <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {STAGES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <textarea className="w-full mt-1 border rounded-lg px-3 py-2 text-sm bg-background" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="lead-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
